import { ApiError, api } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatUsd } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input, Spinner, StatCard, StatCards, Table, TableWrap, Td, Th } from '../../ui';
import { WITHDRAWAL_TABS } from './WithdrawalList';

const SETTINGS_KEY = ['lds1-purchase-settings'];
const REPORT_KEY = ['lds1-purchase-report'];

/**
 * 2026-09-11, LDS1 subdomain only: client's explicit requirement, verbatim —
 * an already-active member pays USDT to the company's deposit wallet, and
 * the platform automatically sends the matching LDS1 to a wallet they
 * specify. This is the ONLY place the LDS1-sender hot wallet's private key
 * is ever entered — completely independent of the withdrawal hot wallet on
 * the "Auto Withdrawal" tab (separate settings, separate key, separate caps).
 * Same write-only key discipline as AutoWithdrawal.tsx: the key field is
 * always blank on load, and only the derived address is ever shown back.
 */
export function Lds1Purchase() {
  const queryClient = useQueryClient();
  const [keyInput, setKeyInput] = useState('');
  const [keySavedAddress, setKeySavedAddress] = useState<string | null>(null);
  const [depositAddressInput, setDepositAddressInput] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.lds1Purchase.admin.settings().then((r) => r.data),
  });

  const { data: report } = useQuery({
    queryKey: REPORT_KEY,
    queryFn: () => api.lds1Purchase.admin.report().then((r) => r.data),
  });

  const settingsForm = useForm<{
    enabled: boolean;
    max_per_tx_usd: string;
    max_per_day_usd: string;
    gas_limit_units: string;
  }>({
    values: settings
      ? {
          enabled: settings.enabled,
          max_per_tx_usd: settings.max_per_tx_usd,
          max_per_day_usd: settings.max_per_day_usd,
          gas_limit_units: String(settings.gas_limit_units),
        }
      : undefined,
  });

  const saveSettings = useMutation({
    mutationFn: (values: { enabled: boolean; max_per_tx_usd: string; max_per_day_usd: string; gas_limit_units: string }) =>
      api.lds1Purchase.admin.updateSettings({
        enabled: values.enabled,
        max_per_tx_usd: values.max_per_tx_usd,
        max_per_day_usd: values.max_per_day_usd,
        gas_limit_units: Number(values.gas_limit_units),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });

  const saveKey = useMutation({
    mutationFn: (privateKey: string) => api.lds1Purchase.admin.setWalletKey(privateKey),
    onSuccess: (res) => {
      setKeyInput('');
      setKeySavedAddress(res.data.wallet_address);
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });

  const clearKey = useMutation({
    mutationFn: () => api.lds1Purchase.admin.clearWalletKey(),
    onSuccess: () => {
      setKeySavedAddress(null);
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });

  const retrySend = useMutation({
    mutationFn: (id: string) => api.lds1Purchase.admin.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORT_KEY });
    },
  });

  const saveDepositAddress = useMutation({
    mutationFn: (address: string) => api.lds1Purchase.admin.updateDepositAddress(address),
    onSuccess: () => {
      setDepositAddressInput('');
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="LDS1 Purchase"
        description="Automatic USDT-to-LDS1 sends — hot wallet, kill switch, and caps (LDS1 subdomain only)"
        tabs={WITHDRAWAL_TABS}
      />

      <Card>
        <h3 className="card-title">Company deposit address (for members paying USDT)</h3>
        <p>
          <strong>{settings?.deposit_address || '— not configured —'}</strong>
        </p>
        <p className="hint">
          Members send USDT here to buy LDS1. This is the same deposit address used for the LDS1
          activation/registration flow — changing it here changes it everywhere on this subdomain.
        </p>

        {saveDepositAddress.isSuccess ? <Alert tone="success">Deposit address updated.</Alert> : null}
        {saveDepositAddress.isError ? (
          <Alert tone="danger">{(saveDepositAddress.error as ApiError)?.message ?? 'Failed to update the address.'}</Alert>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (depositAddressInput.trim()) saveDepositAddress.mutate(depositAddressInput.trim());
          }}
          noValidate
        >
          <FormGroup label="New deposit address">
            <Input
              placeholder="0x…"
              value={depositAddressInput}
              onChange={(e) => setDepositAddressInput(e.target.value)}
            />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={saveDepositAddress.isPending || !depositAddressInput.trim()}>
            {saveDepositAddress.isPending ? 'Saving…' : 'Update Deposit Address'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Kill switch &amp; safety caps</h3>
        {saveSettings.isSuccess ? <Alert tone="success">Settings saved.</Alert> : null}
        {saveSettings.isError ? (
          <Alert tone="danger">{(saveSettings.error as ApiError)?.message ?? 'Failed to save settings.'}</Alert>
        ) : null}

        <form onSubmit={settingsForm.handleSubmit((values) => saveSettings.mutate(values))} noValidate>
          <FormGroup label="Automatic LDS1 purchase enabled">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" {...settingsForm.register('enabled')} />
              <span>
                {settingsForm.watch('enabled')
                  ? 'ON — verified USDT payments within caps get LDS1 sent automatically.'
                  : 'OFF (kill switch) — USDT payments are verified and recorded, but no LDS1 is sent.'}
              </span>
            </label>
          </FormGroup>

          <FormGroup label="Max per transaction (USD)">
            <Input type="number" step="any" min="0" {...settingsForm.register('max_per_tx_usd', { required: true })} />
          </FormGroup>

          <FormGroup label="Max per day, total across all members (USD)">
            <Input type="number" step="any" min="0" {...settingsForm.register('max_per_day_usd', { required: true })} />
          </FormGroup>

          <FormGroup label="Gas limit (units)">
            <Input type="number" step="1" min="21000" max="1000000" {...settingsForm.register('gas_limit_units', { required: true })} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={saveSettings.isPending}>
            {saveSettings.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        </form>
        <p className="hint">
          A confirmed USDT payment above either cap, or received while the switch is off, is left
          pending (not failed) — the USDT is on record, but nothing is sent until an admin retries it
          or raises the caps.
        </p>
      </Card>

      <Card>
        <h3 className="card-title">LDS1-sender hot wallet</h3>
        <p className="hint">
          Completely separate from the withdrawal hot wallet on the Auto Withdrawal tab — its own key,
          its own balance, its own caps.
        </p>

        {settings?.has_key ? (
          <>
            <p>
              <strong>Address:</strong> {settings.wallet_address}
            </p>
            <p>
              <strong>Key set:</strong> {settings.key_set_at ?? '—'}
            </p>
            <StatCards>
              <StatCard
                label="BNB balance (gas)"
                value={settings.sender_wallet_bnb_balance ? `${settings.sender_wallet_bnb_balance} BNB` : '—'}
              />
              <StatCard
                label="LDS1 balance"
                value={settings.sender_wallet_lds1_balance ?? '—'}
              />
            </StatCards>
            <Button
              variant="danger"
              onClick={() => {
                if (
                  window.confirm(
                    'Clear the LDS1-sender wallet key? Automatic purchases will stop signing anything until a new key is set.',
                  )
                ) {
                  clearKey.mutate();
                }
              }}
              disabled={clearKey.isPending}
            >
              {clearKey.isPending ? 'Clearing…' : 'Clear key'}
            </Button>
          </>
        ) : (
          <Alert tone="warning">No LDS1-sender wallet key configured — automatic purchases cannot send anything yet.</Alert>
        )}

        {keySavedAddress ? (
          <Alert tone="success">
            Key saved. Derived address: {keySavedAddress}. Verify this matches the wallet you intended,
            and that it holds enough LDS1 and BNB (gas), before enabling the kill switch above.
          </Alert>
        ) : null}
        {saveKey.isError ? (
          <Alert tone="danger">{(saveKey.error as ApiError)?.message ?? 'Failed to save key.'}</Alert>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (keyInput.trim()) saveKey.mutate(keyInput.trim());
          }}
          noValidate
        >
          <FormGroup label={settings?.has_key ? 'Replace private key' : 'Private key'}>
            <Input
              type="password"
              autoComplete="off"
              placeholder="64 hex characters, 0x prefix optional"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
            />
          </FormGroup>
          <p className="hint">
            This field never shows the currently-saved key — the server stores it encrypted and never
            returns it again, to anyone, for any reason. Only the wallet address it derives to is ever
            shown back to you.
          </p>
          <Button type="submit" variant="gold" disabled={saveKey.isPending || !keyInput.trim()}>
            {saveKey.isPending ? 'Saving…' : 'Save key'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Report</h3>
        {report ? (
          <>
            <StatCards>
              <StatCard label="Sent (total)" value={String(report.sent_count)} />
              <StatCard label="Sent USD (total)" value={formatUsd(report.sent_total_usd)} />
              <StatCard label="Sent USD (today)" value={formatUsd(report.sent_today_usd)} />
              <StatCard label="Pending" value={String(report.pending_count)} />
              <StatCard label="Failed sends" value={String(report.failed_count)} />
            </StatCards>

            {report.failed.length > 0 ? (
              <>
                <h4>Failed auto-sends — need a manual decision</h4>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>User</Th>
                        <Th>Recipient</Th>
                        <Th>USDT confirmed</Th>
                        <Th>LDS1</Th>
                        <Th>Reason</Th>
                        <Th>Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.failed.map((row) => (
                        <tr key={row.id}>
                          <Td>{row.user_code ?? '—'}</Td>
                          <Td wrap>{row.recipient_wallet_address}</Td>
                          <Td>{formatUsd(row.usdt_amount_confirmed)}</Td>
                          <Td>{row.lds1_amount ?? '—'}</Td>
                          <Td wrap>{row.reject_reason ?? '—'}</Td>
                          <Td>
                            <Chip as="button" tone="gold" onClick={() => retrySend.mutate(row.id)}>
                              {retrySend.isPending && retrySend.variables === row.id ? 'Retrying…' : 'Retry'}
                            </Chip>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </>
            ) : null}

            {report.pending.length > 0 ? (
              <>
                <h4>Pending — USDT received, LDS1 not yet sent</h4>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>User</Th>
                        <Th>Recipient</Th>
                        <Th>USDT expected</Th>
                        <Th>USDT confirmed</Th>
                        <Th>Tx hash</Th>
                        <Th>Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.pending.map((row) => (
                        <tr key={row.id}>
                          <Td>{row.user_code ?? '—'}</Td>
                          <Td wrap>{row.recipient_wallet_address}</Td>
                          <Td>{formatUsd(row.usdt_amount_expected)}</Td>
                          <Td>{formatUsd(row.usdt_amount_confirmed)}</Td>
                          <Td wrap>{row.tx_hash ?? '—'}</Td>
                          <Td>
                            {row.tx_hash ? (
                              <Chip as="button" tone="gold" onClick={() => retrySend.mutate(row.id)}>
                                {retrySend.isPending && retrySend.variables === row.id ? 'Sending…' : 'Send now'}
                              </Chip>
                            ) : (
                              <span className="hint">awaiting payment</span>
                            )}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </>
            ) : null}
          </>
        ) : (
          <Spinner />
        )}
      </Card>
    </>
  );
}
